/**
 * Frontend Validation Utilities
 * 
 * Client-side validation for UX (duplicates backend validation):
 * - Email format validation
 * - GUID format validation
 * - Input sanitization
 */

/**
 * Email validation regex (RFC 5322 simplified)
 */
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/**
 * GUID validation regex (UUID v4)
 */
const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Validate email address format
 * 
 * @param {string} email - Email address to validate
 * @returns {boolean} True if valid email format
 */
export function isValidEmail(email) {
  if (typeof email !== 'string' || email.length === 0 || email.length > 255) {
    return false;
  }
  return EMAIL_REGEX.test(email);
}

/**
 * Validate GUID format (UUID v4)
 * 
 * @param {string} guid - GUID to validate
 * @returns {boolean} True if valid GUID format
 */
export function isValidGuid(guid) {
  if (typeof guid !== 'string') {
    return false;
  }
  return GUID_REGEX.test(guid);
}

/**
 * Sanitize email address
 * 
 * @param {string} email - Email address to sanitize
 * @returns {string} Sanitized email
 */
export function sanitizeEmail(email) {
  if (typeof email !== 'string') {
    return '';
  }
  return email.trim().toLowerCase();
}

/**
 * Sanitize GUID
 * 
 * @param {string} guid - GUID to sanitize
 * @returns {string} Sanitized GUID
 */
export function sanitizeGuid(guid) {
  if (typeof guid !== 'string') {
    return '';
  }
  return guid.trim().toLowerCase();
}

/**
 * Validate and sanitize email
 * 
 * @param {string} email - Email to validate and sanitize
 * @returns {{ valid: boolean, email: string, error?: string }} Validation result
 */
export function validateEmail(email) {
  const sanitized = sanitizeEmail(email);

  if (!sanitized) {
    return { valid: false, email: '', error: 'Email is required' };
  }

  if (!isValidEmail(sanitized)) {
    return { valid: false, email: sanitized, error: 'Invalid email format' };
  }

  return { valid: true, email: sanitized };
}

/**
 * Validate and sanitize GUID
 * 
 * @param {string} guid - GUID to validate and sanitize
 * @returns {{ valid: boolean, guid: string, error?: string }} Validation result
 */
export function validateGuid(guid) {
  const sanitized = sanitizeGuid(guid);

  if (!sanitized) {
    return { valid: false, guid: '', error: 'Request code is required' };
  }

  if (!isValidGuid(sanitized)) {
    return { valid: false, guid: sanitized, error: 'Invalid request code format' };
  }

  return { valid: true, guid: sanitized };
}

/**
 * Salesforce Case Id validation regex (15- or 18-character alphanumeric)
 */
const CASE_ID_REGEX = /^[a-zA-Z0-9]{15}$|^[a-zA-Z0-9]{18}$/;

/**
 * Validate and sanitize a Salesforce Case Id
 *
 * @param {string} caseId - Case Id to validate
 * @returns {{ valid: boolean, caseId: string, error?: string }} Validation result
 */
export function validateCaseId(caseId) {
  const sanitized = typeof caseId === 'string' ? caseId.trim() : '';

  if (!sanitized) {
    return { valid: false, caseId: '', error: 'Case ID is required' };
  }

  if (!CASE_ID_REGEX.test(sanitized)) {
    return { valid: false, caseId: sanitized, error: 'Invalid Case ID format' };
  }

  return { valid: true, caseId: sanitized };
}

export default {
  isValidEmail,
  isValidGuid,
  sanitizeEmail,
  sanitizeGuid,
  validateEmail,
  validateGuid,
  validateCaseId,
};
